import { useRef } from "react";
import { KeyboardArrowDownRounded } from "@mui/icons-material";

import { Popper, Text } from "@fragments";

import { SUBSCRIPTION_STATUS_LABEL, SUBSCRIPTION_STATUS_TONE } from "../../../enums";

import "./styles.scss";

function normalizeList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return Object.values(raw);
  return [];
}

function sortByPriority(subs) {
  const score = (sub) => (sub?.status === "active" ? 0 : 1);
  return [...subs].sort((a, b) => score(a) - score(b));
}

function getPlanLabel(planId, plans) {
  const plan = plans?.find?.((p) => p.id === planId);
  return plan?.label || `Plano ${planId ?? ""}`.trim();
}

function formatDate(raw) {
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function getStatusDateSuffix(sub) {
  if (sub?.status === "active") {
    const formatted = formatDate(sub?.timestamps?.expiration);
    return formatted ? `até ${formatted}` : null;
  }
  if (sub?.status === "canceled") {
    const formatted = formatDate(sub?.timestamps?.canceled);
    return formatted ? `em ${formatted}` : null;
  }
  return null;
}

function resolveSubscriptionParts(sub, plans) {
  const tone = SUBSCRIPTION_STATUS_TONE[sub.status] || "neutral";
  const planLabel = sub.plan?.label || getPlanLabel(sub.plan_id, plans);
  const statusLabel = SUBSCRIPTION_STATUS_LABEL[sub.status] || "Pendente";
  const dateSuffix = getStatusDateSuffix(sub);
  return { tone, planLabel, statusLabel, dateSuffix };
}

function SubscriptionBadge({ sub, plans }) {
  const { tone, planLabel, statusLabel, dateSuffix } = resolveSubscriptionParts(sub, plans);

  return (
    <div className={`members-list-subscription members-list-subscription--${tone}`}>
      <Text.span contrast={8}>
        {planLabel}
      </Text.span>
      <span className="members-list-subscription__dot" />
      <Text.span contrast={4}>
        {dateSuffix ? `${statusLabel} ${dateSuffix}` : statusLabel}
      </Text.span>
    </div>
  );
}

export default function SubscriptionCell({ member, plans }) {
  const popperRef = useRef(null);

  const subscriptions = sortByPriority(
    normalizeList(member?.subscriptions ?? member?.subscription)
  );

  if (!subscriptions.length) {
    return <Text.span contrast={6}>—</Text.span>;
  }

  const [primary, ...rest] = subscriptions;
  const extra = rest.length;

  function openPopper(event) {
    event.stopPropagation();
    const options = subscriptions.map((sub, index) => {
      const { planLabel, statusLabel, dateSuffix } = resolveSubscriptionParts(sub, plans);
      return {
        id: sub.id ?? `${sub.plan_id}-${index}`,
        label: planLabel,
        right: (
          <Text.span contrast={4}>
            {dateSuffix ? `${statusLabel} ${dateSuffix}` : statusLabel}
          </Text.span>
        ),
        noHover: true,
      };
    });

    popperRef.current?.open(options, "bottom-end", event.currentTarget);
  }

  return (
    <div className="members-list-subscription-cell">
      <SubscriptionBadge sub={primary} plans={plans} />
      {extra > 0 && (
        <>
          <button
            type="button"
            className="members-list-subscription-more"
            onClick={openPopper}
          >
            <Text.span contrast={6}>
              +{extra} {extra === 1 ? "assinatura" : "assinaturas"}
            </Text.span>
            <KeyboardArrowDownRounded className="members-list-subscription-more__caret" />
          </button>
          <Popper controlRef={popperRef} />
        </>
      )}
    </div>
  );
}
